import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@artstorefronts/ui';
import { Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';

import AdminLayout from '../../../components/AdminLayout';
import {
  ADMIN_PRE_FEEDS,
  ADMIN_CREATE_PRE_FEED,
  ADMIN_SET_PRE_FEED_STATUS,
  audienceSummary,
  STATUS_PILL_CLASSES,
  type AdminPreFeed,
} from './pre-feed-shared';

function StatusPill({ status }: { status: string }) {
  return (
    <span className={`rounded-full px-2 py-1 text-xs font-medium ${STATUS_PILL_CLASSES[status] ?? ''}`}>
      {status.toLowerCase()}
    </span>
  );
}

function AdminPreFeedsPage() {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [confirmStatus, setConfirmStatus] = useState<{ feed: AdminPreFeed; next: 'LIVE' | 'DRAFT' } | null>(null);

  const { data, loading, refetch } = useQuery(ADMIN_PRE_FEEDS, { fetchPolicy: 'cache-and-network' });
  const [createPreFeed, { loading: creating }] = useMutation(ADMIN_CREATE_PRE_FEED);
  const [setStatus, { loading: settingStatus }] = useMutation(ADMIN_SET_PRE_FEED_STATUS);

  const feeds: AdminPreFeed[] = data?.adminPreFeeds ?? [];

  async function handleCreate() {
    if (!name.trim()) {
      toast.error('Name is required.');
      return;
    }
    try {
      const result = await createPreFeed({ variables: { input: { name: name.trim() } } });
      const created = result.data?.adminCreatePreFeed;
      toast.success('Pre-feed created as draft.');
      setCreateOpen(false);
      setName('');
      if (created?.id) {
        navigate(`/admin/pre-feeds/${created.id}`);
      } else {
        void refetch();
      }
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to create pre-feed.');
    }
  }

  async function handleConfirmStatus() {
    if (!confirmStatus) return;
    try {
      await setStatus({ variables: { id: confirmStatus.feed.id, status: confirmStatus.next } });
      toast.success(confirmStatus.next === 'LIVE' ? 'Pre-feed is now LIVE.' : 'Pre-feed set back to draft.');
      setConfirmStatus(null);
      void refetch();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to change status.');
    }
  }

  return (
    <AdminLayout
      title="Pre-Feeds"
      description="Curated post lists served ahead of the live trending feed. Draft feeds are invisible; one live global feed at a time, plus audience-targeted feeds."
      action={
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New pre-feed
        </Button>
      }
    >
      {loading && feeds.length === 0 ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : feeds.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
          No pre-feeds yet. Create one to get started.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <div className="grid grid-cols-[1fr_90px_1fr_140px_70px_70px_110px_160px] items-center gap-2 border-b border-gray-200 bg-gray-50 px-4 py-2 text-xs font-medium uppercase tracking-wide text-gray-500">
            <div>Name</div>
            <div>Status</div>
            <div>Audience</div>
            <div title="Users the audience + exclusions select, of all users">Aud. size</div>
            <div>Priority</div>
            <div>Posts</div>
            <div>Published</div>
            <div />
          </div>
          {feeds.map((feed) => (
            <div
              key={feed.id}
              className="grid grid-cols-[1fr_90px_1fr_140px_70px_70px_110px_160px] items-center gap-2 border-b border-gray-100 px-4 py-3 text-sm last:border-b-0"
            >
              <Link to={`/admin/pre-feeds/${feed.id}`} className="font-medium text-gray-900 hover:underline">
                {feed.name}
              </Link>
              <div>
                <StatusPill status={feed.status} />
              </div>
              <div className="truncate text-gray-600" title={audienceSummary(feed)}>
                {audienceSummary(feed)}
              </div>
              <div className="text-gray-600" title="Users the audience + exclusions select, of all users">
                {feed.audienceCount != null && feed.audienceTotal != null
                  ? `${feed.audienceCount.toLocaleString()} of ${feed.audienceTotal.toLocaleString()}`
                  : '—'}
              </div>
              <div className="text-gray-600">{feed.priority}</div>
              <div className="text-gray-600">{feed.postCount}</div>
              <div className="text-gray-500">
                {feed.publishedAt ? new Date(feed.publishedAt).toLocaleDateString() : '—'}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => navigate(`/admin/pre-feeds/${feed.id}`)}>
                  Edit
                </Button>
                {feed.status === 'LIVE' ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirmStatus({ feed, next: 'DRAFT' })}
                    disabled={settingStatus}
                  >
                    Set draft
                  </Button>
                ) : (
                  <Button size="sm" onClick={() => setConfirmStatus({ feed, next: 'LIVE' })} disabled={settingStatus}>
                    Go live
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New pre-feed</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="pre-feed-name" className="mb-1 block text-sm">
                Name
              </Label>
              <Input
                id="pre-feed-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Greatest Hits — Hobbyists"
              />
            </div>
            <p className="text-xs text-gray-500">
              The feed is created as a DRAFT. Audience, exclusions, and generation criteria are set in the editor.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmStatus} onOpenChange={(open) => !open && setConfirmStatus(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmStatus?.next === 'LIVE'
                ? `Set "${confirmStatus?.feed.name}" live?`
                : `Set "${confirmStatus?.feed.name}" back to draft?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmStatus?.next === 'LIVE'
                ? 'Users matching this feed will start seeing it as their pre-feed immediately.'
                : 'Users will stop seeing this pre-feed immediately and fall back to the global feed or live trending.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={settingStatus}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmStatus} disabled={settingStatus}>
              {settingStatus ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}

export default AdminPreFeedsPage;
